import paymentsService from '@/modules/payments/services/paymentsService';
import {
  DEFAULT_PER_PAGE,
  PAYMENT_PLANS_TABS,
  TAB_DEFAULT_SORT,
  getInitialFilters,
} from '@/modules/payments/constants/paymentPlans';
import { buildPaymentPlansQuery, getActiveFilterKeys } from '@/modules/payments/utils/paymentPlansQueryUtil';
import logError from '@/utils/logError/logError';

let latestRequestId = 0;

const state = {
  tab: PAYMENT_PLANS_TABS.plans,
  query: { page: 1, perPage: DEFAULT_PER_PAGE, sort: { ...TAB_DEFAULT_SORT[PAYMENT_PLANS_TABS.plans] } },
  filters: getInitialFilters(),
  plans: [],
  total: 0,
  isLoaded: false,
  isLoading: false,
  hasError: false,
};

const getters = {
  tab: (state) => state.tab,
  query: (state) => state.query,
  filters: (state) => state.filters,
  plans: (state) => state.plans,
  total: (state) => state.total,
  isLoading: (state) => state.isLoading,
  hasError: (state) => state.hasError,
  activeFilterKeys: (state) => getActiveFilterKeys(state.filters, state.tab),
  hasActiveFilters: (state, getters) => getters.activeFilterKeys.length > 0,
  hasNoRows: (state) => state.isLoaded && !state.isLoading && !state.hasError && state.total === 0,
  isEmptyBusiness: (state, getters) => getters.hasNoRows && !getters.hasActiveFilters,
  hasNoMatches: (state, getters) => getters.hasNoRows && getters.hasActiveFilters,
};

const mutations = {
  setTab(state, tab) {
    state.tab = tab;
  },
  setQuery(state, changes) {
    state.query = { ...state.query, ...changes };
  },
  setFilter(state, { key, value }) {
    state.filters = { ...state.filters, [key]: value };
  },
  resetFilters(state) {
    state.filters = getInitialFilters();
  },
  setPlans(state, { plans, total }) {
    state.plans = plans;
    state.total = total;
    state.isLoaded = true;
  },
  resetPlans(state) {
    state.plans = [];
    state.total = 0;
    state.isLoaded = false;
  },
  setIsLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
  setHasError(state, hasError) {
    state.hasError = hasError;
  },
};

const actions = {
  async fetchPlans({ state, commit, dispatch }) {
    latestRequestId += 1;
    const requestId = latestRequestId;
    commit('setIsLoading', true);
    commit('setHasError', false);
    try {
      const { payment_plans: plans, total_payment_plans: total } = await paymentsService.fetchPaymentPlans(buildPaymentPlansQuery(state));
      if (requestId !== latestRequestId) return;
      const lastPage = Math.max(Math.ceil(total / state.query.perPage), 1);
      if (!plans.length && state.query.page > lastPage) {
        commit('setQuery', { page: lastPage });
        dispatch('fetchPlans');
        return;
      }
      commit('setPlans', { plans, total });
    } catch (error) {
      if (requestId !== latestRequestId) return;
      commit('setHasError', true);
      logError('Failed to fetch the payment plans list', error);
    } finally {
      if (requestId === latestRequestId) commit('setIsLoading', false);
    }
  },
  setTab({ commit, dispatch }, tab) {
    commit('setTab', tab);
    commit('resetPlans');
    commit('setQuery', { page: 1, sort: { ...TAB_DEFAULT_SORT[tab] } });
    return dispatch('fetchPlans');
  },
  setSort({ commit, dispatch }, sort) {
    commit('setQuery', { sort, page: 1 });
    return dispatch('fetchPlans');
  },
  setPage({ commit, dispatch }, page) {
    commit('setQuery', { page });
    return dispatch('fetchPlans');
  },
  setPerPage({ commit, dispatch }, perPage) {
    commit('setQuery', { perPage, page: 1 });
    return dispatch('fetchPlans');
  },
  setFilter({ commit, dispatch }, { key, value }) {
    commit('setFilter', { key, value });
    commit('setQuery', { page: 1 });
    return dispatch('fetchPlans');
  },
  removeFilter({ commit, dispatch }, key) {
    commit('setFilter', { key, value: getInitialFilters()[key] });
    commit('setQuery', { page: 1 });
    return dispatch('fetchPlans');
  },
  clearFilters({ commit, dispatch }) {
    commit('resetFilters');
    commit('setQuery', { page: 1 });
    return dispatch('fetchPlans');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
